import { getS3Url, sendS3Url } from "../../utilities/items-service";
import { thumbUrl, makeThumbnailBlob } from "../../utilities/image-urls";
import { useRef, useState } from "react";
import Loader from "../Loader/Loader";
import { FaImage } from "react-icons/fa";
import { HiXMark } from "react-icons/hi2";

// Ordered list of an item's images. The first image is the primary one —
// it drives the homepage tile, the featured carousel, and the framing
// editor. Drag thumbnails to reorder; × removes an image.
export default function ImageForm({ images, setImages }) {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  async function handleFilesChange(evt) {
    const files = Array.from(evt.target.files || []);
    evt.target.value = ""; // allow re-selecting the same file later
    if (files.length === 0) return;
    setIsUploading(true);
    for (const file of files) {
      try {
        const { url, thumbUrl: thumbUploadUrl } = await getS3Url(file.type);
        const uploadedUrl = await sendS3Url(url, file);
        // The services return the Error on failure instead of throwing
        if (typeof uploadedUrl === "string") {
          setImages((prev) => [...prev, uploadedUrl]);
          // Best-effort tile thumbnail — tiles fall back to the
          // original if this fails, so never block the upload on it
          if (thumbUploadUrl) {
            const thumbBlob = await makeThumbnailBlob(file);
            if (thumbBlob) await sendS3Url(thumbUploadUrl, thumbBlob);
          }
        }
      } catch (error) {
        console.log(error);
      }
    }
    setIsUploading(false);
  }

  function moveImage(from, to) {
    if (from === to || from == null || to == null) return;
    setImages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  function removeImage(index) {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }

  function handleDrop(index) {
    moveImage(dragIndex, index);
    setDragIndex(null);
    setOverIndex(null);
  }

  return (
    <section>
      <input
        ref={fileInputRef}
        type="file"
        name="images"
        accept="image/*"
        multiple
        onChange={handleFilesChange}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="inline-flex items-center gap-2 rounded-lg bg-seal px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-seal-deep hover:shadow-md active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-seal focus:ring-offset-2"
      >
        <FaImage className="h-4 w-4" />
        {images.length > 0 ? "Add images" : "Upload images"}
      </button>
      {isUploading && (
        <div className="mt-3">
          <Loader />
        </div>
      )}
      {images.length > 0 && (
        <>
          <ul className="mt-3 flex flex-wrap gap-3">
            {images.map((url, index) => (
              <li
                key={url}
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragEnd={() => {
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                onDragOver={(evt) => {
                  evt.preventDefault(); // allow dropping here
                  setOverIndex(index);
                }}
                onDragLeave={() =>
                  setOverIndex((prev) => (prev === index ? null : prev))
                }
                onDrop={() => handleDrop(index)}
                className={`relative w-40 rounded-lg border bg-cream transition-all cursor-grab active:cursor-grabbing ${
                  overIndex === index && dragIndex !== index
                    ? "border-seal ring-2 ring-seal/40"
                    : "border-ink/15"
                } ${dragIndex === index ? "opacity-40" : ""}`}
              >
                <img
                  src={thumbUrl(url) || url}
                  alt={`Item image ${index + 1}`}
                  draggable={false}
                  onError={(evt) => {
                    // Thumb missing (legacy image) — fall back to the original
                    if (evt.currentTarget.src !== url)
                      evt.currentTarget.src = url;
                  }}
                  className="h-40 w-40 rounded-lg object-cover pointer-events-none"
                />
                {index === 0 && (
                  <span className="absolute bottom-1 left-1 rounded bg-ink/80 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-paper pointer-events-none">
                    Primary
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  aria-label={`Remove image ${index + 1}`}
                  className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full border border-ink/20 bg-paper text-ink/60 shadow-sm transition-colors hover:border-seal hover:text-seal"
                >
                  <HiXMark className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
          <p className="text-xs text-ink/40 mt-2">
            {images.length > 1
              ? "Drag to reorder. The first image is the primary — it appears on tiles and in the carousel. Every image can be framed for the detail view below."
              : "The image appears on tiles and in the carousel."}
          </p>
        </>
      )}
    </section>
  );
}
